/**
 * Whether this element is itself a scroll container with somewhere left to go in the
 * direction a key is about to move focus.
 *
 * The board takes the arrow keys to move its roving item, and it must not take one from
 * an element that is still using it. The case that matters is a cell holding no cards
 * and more content than fits: a tall empty state, or the page of skeletons a cell draws
 * while its first page is in flight. Its body is the board's roving item, so it holds
 * focus, and the only way to read what is below its fold is to scroll it.
 *
 * Only the element focus is actually on, never an ancestor. A focused card sits inside a
 * cell that can nearly always scroll, so asking about ancestors would mean the arrow keys
 * stopped moving focus on any board big enough to need them.
 *
 * `scrollLeft` is negative in a right-to-left container, so the inline axis is measured
 * on the distance from the start edge rather than on the sign of the offset.
 */
export function canScrollFurther(
  target: EventTarget | null,
  axis: 'block' | 'inline',
  towards: 'start' | 'end',
): boolean {
  if (!(target instanceof HTMLElement)) return false

  // Overflowing is not the same as scrolling. An element whose overflow is `visible` or
  // `hidden` still reports a scroll size larger than its client size, and the browser
  // will not scroll it for a key, so answering yes there would leave the reader pressing
  // a key that does nothing: the board declined it and nothing else took it. A cell's
  // body is exactly this case on the inline axis, where it states `hidden`.
  //
  // Physical axes rather than `overflow-block` and `overflow-inline`, which are not
  // reliably readable back from a computed style. The board is horizontal-tb throughout,
  // so the two agree.
  const style = target.ownerDocument.defaultView?.getComputedStyle(target)
  const overflow = axis === 'block' ? style?.overflowY : style?.overflowX
  if (overflow !== 'auto' && overflow !== 'scroll') return false

  const [offset, visible, total] =
    axis === 'block'
      ? [target.scrollTop, target.clientHeight, target.scrollHeight]
      : [Math.abs(target.scrollLeft), target.clientWidth, target.scrollWidth]

  // Both are fractional on a zoomed page or under a device pixel ratio that is not a
  // whole number, so the ends are never exactly equal.
  const SUB_PIXEL = 1

  return towards === 'start' ? offset > SUB_PIXEL : offset + visible < total - SUB_PIXEL
}
